import React, { useState, useMemo, useEffect } from 'react';
import { useLayoutContext } from '../../components/layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import { Student, Teacher, ClassSetting, Role, isTeacher } from '../../utils/firebase';
import { doc, updateDoc, addDoc, collection, query, where, getDocs, deleteDoc, getDoc, writeBatch, onSnapshot, setDoc, collectionGroup, serverTimestamp } from 'firebase/firestore';
import { db, COLLECTIONS } from '../../utils/firebase';
import { useNavigate } from 'react-router-dom';
import { createApprovedStudentDoc } from '../../utils/approveStudent';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import InputField from '../../components/ui/InputField';
import EditIcon from '../../components/icons/EditIcon';
import TrashIcon from '../../components/icons/TrashIcon';
import QrCodeIcon from '../../components/icons/QrCodeIcon';
import SettingIcon from '../../components/icons/SettingIcon';
import SearchIcon from '../../components/icons/SearchIcon';
import CheckCircleIcon from '../../components/icons/CheckCircleIcon';
import CloseIcon from '../../components/icons/CloseIcon';
import ShowIcon from '../../components/icons/ShowIcon';
import HideIcon from '../../components/icons/HideIcon';

interface FormData {
  name: string;
  email: string;
  password: string;
  showPassword: boolean;
  grade: number;
  class: number;
  number: number;
  loginType: 'local' | 'social';
  username: string;
}

interface SchoolInfo {
  schoolLevel: '초등학교' | '중학교' | '고등학교';
  gradeClasses: { [key: number]: number };
}

const GameStudentManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const { isMobile } = useLayoutContext();
  const teacher = currentUser as Teacher;
  const navigate = useNavigate();
  
  // 로그인하지 않은 경우 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // 로그인하지 않은 경우에도 페이지를 볼 수 있도록 처리
  const isLoggedIn = !!currentUser;
  
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedClass, setSelectedClass] = useState<ClassSetting | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [classSettings, setClassSettings] = useState<ClassSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    showPassword: false,
    grade: 0,
    class: 0,
    number: 0,
    loginType: 'local',
    username: ''
  });
  const [newStudent, setNewStudent] = useState<{ name: string; studentId: string }>({ name: '', studentId: '' });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openSubjectCodeDialog, setOpenSubjectCodeDialog] = useState(false);
  const [subjectCode, setSubjectCode] = useState<string | null>(null);
  const [selectedClasses, setSelectedClasses] = useState<Record<string, boolean>>({});
  const [schoolInfo, setSchoolInfo] = useState<SchoolInfo>({
    schoolLevel: '초등학교',
    gradeClasses: {}
  });
  const [pendingStudents, setPendingStudents] = useState<Student[]>([]);

  const showErrorAlert = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 3000);
  };

  const showSuccessAlert = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  // 선생님 설정 불러오기
  useEffect(() => {
    const loadTeacherSettings = async () => {
      if (!currentUser?.uid) return;

      try {
        const teacherRef = doc(db, COLLECTIONS.TEACHERS, currentUser.uid);
        const teacherDoc = await getDoc(teacherRef);
        
        if (teacherDoc.exists()) {
          const data = teacherDoc.data();
          if (data.classes) {
            const classSelections: Record<string, boolean> = {};
            data.classes.forEach((cls: { grade: number; class: number }) => {
              classSelections[`${cls.grade}_${cls.class}`] = true;
            });
            setSelectedClasses(classSelections);
          }
        }
      } catch (error) {
        console.error('선생님 설정을 불러오는 중 오류 발생:', error);
        showErrorAlert('선생님 설정을 불러오는데 실패했습니다.');
      }
    };

    loadTeacherSettings();
  }, [currentUser?.uid]);

  // 교사 정보 실시간 감시
  useEffect(() => {
    if (!isLoggedIn || !teacher?.uid || !isTeacher(teacher)) {
      setLoading(false);
      return;
    }

    const teacherRef = doc(db, `schools/${teacher.schoolId}/teachers/${teacher.uid}`);
    const unsubscribeTeacher = onSnapshot(teacherRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        if (data.stampClasses) {
          setClassSettings(data.stampClasses);
          
          // 도장 학급 설정이 변경되면 선택된 학급 초기화
          if (selectedClass) {
            const isStillValid = data.stampClasses.some(
              (setting: { grade: number; class: number; isActive: boolean }) => 
                Number(setting.grade) === Number(selectedClass.grade) && 
                Number(setting.class) === Number(selectedClass.class) &&
                setting.isActive
            );
            
            if (!isStillValid) {
              setSelectedClass(null);
            }
          }
          
          // stampClasses 데이터를 selectedClasses 형식으로 변환
          const classSelections: Record<string, boolean> = {};
          data.stampClasses.forEach((cls: { grade: number; class: number; isActive: boolean }) => {
            if (cls.isActive) {
              classSelections[`${cls.grade}_${cls.class}`] = true;
            }
          });
          setSelectedClasses(classSelections);
        }
      }
    });

    return () => {
      unsubscribeTeacher();
    };
  }, [teacher?.uid, selectedClass, isLoggedIn]);

  // 학생 목록 실시간 감시
  useEffect(() => {
    if (!isLoggedIn || !teacher?.uid || !isTeacher(teacher)) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching all students for school:', teacher.schoolId);
      
      const studentsQuery = query(
        collectionGroup(db, 'students'),
        where('schoolId', '==', teacher.schoolId),
        where('isActive', '==', true)
      );
      
      console.log('Query parameters:', {
        schoolId: teacher.schoolId,
        isAuthenticated: !!teacher?.uid
      });

      const unsubscribeStudents = onSnapshot(studentsQuery, (snapshot) => {
        console.log('Query snapshot size:', snapshot.size);
        const studentsData = snapshot.docs.map(doc => {
          const data = doc.data();
          // stamps 데이터 처리
          let stampsArray: { subjectId: string; stampCount: number }[] = [];
          if (data.stamps) {
            stampsArray = Array.isArray(data.stamps) ? data.stamps : [];
          }
          
          return {
            uid: doc.id,
            ...data,
            stamps: stampsArray
          };
        }) as Student[];
        
        console.log('Raw students data:', studentsData);
        
        // 교사가 설정한 도장 학급만 필터링
        const filteredStudents = studentsData.filter(student => {
          if (!classSettings || classSettings.length === 0) return true;
          
          const matches = classSettings.some(stampClass => 
            stampClass.isActive && 
            Number(stampClass.grade) === Number(student.grade) && 
            Number(stampClass.class) === Number(student.class)
          );
          
          return matches;
        });
        
        setStudents(filteredStudents);
        setLoading(false);
      });

      return () => {
        unsubscribeStudents();
      };
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('학생 목록을 불러오는데 실패했습니다.');
      setLoading(false);
    }
  }, [teacher?.uid, teacher?.schoolId, classSettings, isLoggedIn]);

  // 학교 정보 가져오기
  useEffect(() => {
    const fetchSchoolInfo = async () => {
      if (!teacher?.schoolId) return;
      
      try {
        const schoolRef = doc(db, COLLECTIONS.SCHOOLS, teacher.schoolId);
        const schoolDoc = await getDoc(schoolRef);
        
        if (schoolDoc.exists()) {
          const data = schoolDoc.data();
          // type 필드를 schoolLevel로 매핑
          const schoolLevelMap: {[key: string]: '초등학교' | '중학교' | '고등학교'} = {
            'elementary': '초등학교',
            'middle': '중학교',
            'high': '고등학교'
          };
          
          setSchoolInfo({
            schoolLevel: schoolLevelMap[data.type] || '초등학교',
            gradeClasses: data.gradeClasses || {}
          });
        }
      } catch (err) {
        console.error('학교 정보 로딩 중 오류:', err);
        setError('학교 정보를 불러오는데 실패했습니다.');
      }
    };
    
    fetchSchoolInfo();
  }, [teacher?.schoolId]);

  const getGradeRange = () => {
    switch (schoolInfo.schoolLevel) {
      case '초등학교':
        return [1, 2, 3, 4, 5, 6];
      case '중학교':
      case '고등학교':
        return [1, 2, 3];
      default:
        return [1, 2, 3, 4, 5, 6];
    }
  };

  const getClassRange = (grade: number) => {
    return Array.from(
      { length: schoolInfo.gradeClasses[grade] || 0 }, 
      (_, i) => i + 1
    );
  };

  const handleClassSelection = (grade: number, classNum: number) => {
    const key = `${grade}_${classNum}`;
    setSelectedClasses(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // 선택된 학급의 학생들만 필터링
  const filteredStudents = useMemo(() => {
    console.log('Filtering students:', { selectedClass, searchQuery });
    return students
      .filter(student => {
        const matchesSearch = searchQuery
          ? student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            student.email.toLowerCase().includes(searchQuery.toLowerCase())
          : true;

        const matchesClass = selectedClass
          ? Number(student.grade) === Number(selectedClass.grade) && Number(student.class) === Number(selectedClass.class)
          : true;

        console.log('Student filter result:', {
          student: student.name,
          grade: student.grade,
          class: student.class,
          selectedGrade: selectedClass?.grade,
          selectedClass: selectedClass?.class,
          matchesClass
        });

        return matchesSearch && matchesClass;
      })
      .sort((a, b) => {
        if (a.grade !== b.grade) return Number(a.grade) - Number(b.grade);
        if (a.class !== b.class) return Number(a.class) - Number(b.class);
        return Number(a.number) - Number(b.number);
      });
  }, [students, searchQuery, selectedClass]);

  const handleOpenDialog = (student?: Student) => {
    if (student) {
      setSelectedStudent(student);
      setFormData({
        name: student.name,
        email: student.email,
        loginType: student.loginType || 'local',
        username: student.username || '',
        password: student.password || '',
        showPassword: false,
        grade: Number(student.grade),
        class: Number(student.class),
        number: Number(student.number)
      });
    } else {
      setSelectedStudent(null);
      setFormData({
        name: '',
        email: '',
        loginType: 'local',
        username: '',
        password: '',
        showPassword: false,
        grade: Number(teacher.homeroomClass?.grade) || 0,
        class: Number(teacher.homeroomClass?.class) || 0,
        number: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedStudent(null);
  };

  const handleOpenSettingsDialog = () => {
    setOpenSettingsDialog(true);
  };

  const handleCloseSettingsDialog = () => {
    setOpenSettingsDialog(false);
  };

  const handleSaveClassSettings = async () => {
    if (!currentUser?.uid || !currentUser.schoolId) return;
    
    try {
      setLoading(true);
      const selectedClassList = Object.entries(selectedClasses)
        .filter(([_, isSelected]) => isSelected)
        .map(([key]) => {
          const [grade, classNum] = key.split('_');
          return { grade: Number(grade), class: Number(classNum), isActive: true };
        });

      // 선택된 클래스 정보를 Firestore에 저장
      const teacherRef = doc(db, `schools/${currentUser.schoolId}/teachers/${currentUser.uid}`);
      await updateDoc(teacherRef, {
        stampClasses: selectedClassList,
        updatedAt: serverTimestamp()
      });
      
      setOpenSettingsDialog(false);
      showSuccessAlert('클래스 설정이 저장되었습니다.');
    } catch (error) {
      console.error('클래스 설정 저장 중 오류 발생:', error);
      showErrorAlert('클래스 설정 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teacher?.uid) return;

    try {
      if (selectedStudent) {
        // 학생 정보 수정
        const studentRef = doc(db, COLLECTIONS.STUDENTS, selectedStudent.uid);
        const updateData: Partial<Student> = {
          name: formData.name,
          email: formData.email,
          grade: Number(formData.grade) || 0,
          class: Number(formData.class) || 0,
          number: Number(formData.number) || 0,
          loginType: formData.loginType,
          username: formData.username,
          updatedAt: new Date(),
          classId: `${teacher.schoolId}_${formData.grade}_${formData.class}`
        };

        await updateDoc(studentRef, updateData);
        showSuccessAlert('학생 정보가 수정되었습니다.');
      } else {
        // 새 학생 추가
        const studentRef = doc(collection(db, COLLECTIONS.STUDENTS));
        const studentData: Omit<Student, 'id'> = {
          name: formData.name,
          email: formData.email,
          schoolId: teacher.schoolId,
          grade: Number(formData.grade) || 0,
          class: Number(formData.class) || 0,
          role: 'student',
          teacherId: teacher.uid,
          number: Number(formData.number) || 0,
          stamps: [],
          points: 0,
          loginType: formData.loginType,
          username: formData.username,
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
          roles: [],
          assignedRoles: [],
          uid: studentRef.id,
          classId: `${teacher.schoolId}_${formData.grade}_${formData.class}`,
          approvalStatus: 'pending'
        };

        await setDoc(studentRef, studentData);
        showSuccessAlert('학생이 추가되었습니다.');
      }

      // 폼 초기화
      setFormData({
        name: '',
        email: '',
        password: '',
        showPassword: false,
        grade: 0,
        class: 0,
        number: 0,
        loginType: 'local',
        username: ''
      });
      setOpenDialog(false);
    } catch (err) {
      console.error('학생 처리 중 오류:', err);
      setError(selectedStudent ? '학생 정보 수정에 실패했습니다.' : '학생을 추가하는데 실패했습니다.');
    }
  };

  const handleDelete = async (student: Student) => {
    setStudentToDelete(student);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!studentToDelete) return;

    try {
      if (!studentToDelete?.uid) return;
      const studentRef = doc(db, COLLECTIONS.STUDENTS, studentToDelete.uid as string);
      await deleteDoc(studentRef);
      setOpenDeleteDialog(false);
      setStudentToDelete(null);
    } catch (err) {
      console.error('학생 삭제 중 오류:', err);
      setError('학생을 삭제하는데 실패했습니다.');
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setStudentToDelete(null);
  };

  const handleAddClass = () => {
    const newClass: ClassSetting = {
      grade: 0,
      class: 0,
      isActive: true
    };
    setClassSettings([...classSettings, newClass]);
  };

  const handleUpdateClass = (index: number, field: keyof ClassSetting, value: any) => {
    const updatedSettings = [...classSettings];
    if (field === 'grade' || field === 'class') {
      value = parseInt(value) || 0;
    }
    updatedSettings[index] = {
      ...updatedSettings[index],
      [field]: value
    };
    setClassSettings(updatedSettings);
  };

  const handleRemoveClass = (index: number) => {
    setClassSettings(classSettings.filter((_, i) => i !== index));
  };

  const handleOpenStampDialog = (student: Student) => {
    setSelectedStudent(student);
  };

  const handleSelectStudent = (studentId: string) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    setSelectedStudents(prev => 
      prev.length === filteredStudents.length 
        ? [] 
        : filteredStudents.map(student => student.uid || '')
    );
  };

  const generateRandomPassword = () => {
    const length = 8;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    setFormData({ ...formData, password });
  };

  // 교과 코드 생성
  const generateSubjectCode = async () => {
    if (!teacher?.subjectId) {
      setError('교과 정보가 없습니다.');
      return;
    }
    
    try {
      setError(null);
      setLoading(true);
      
      // 모든 사용자(교사, 관리자)에 대해 teacher.subjectId 사용
      const subjectId = teacher.subjectId;
      
      // 교과 코드가 이미 있는지 확인
      const subjectCodeRef = collection(db, COLLECTIONS.SUBJECT_CODES);
      const q = query(
        subjectCodeRef,
        where('schoolId', '==', teacher.schoolId),
        where('subjectId', '==', subjectId)
      );
      const querySnapshot = await getDocs(q);

      // 기존 교과 코드가 있으면 재사용, 없으면 새로 생성
      if (!querySnapshot.empty) {
        const existingCode = querySnapshot.docs[0].data().code;
        setSubjectCode(existingCode);
        setOpenSubjectCodeDialog(true);
        return;
      } else {
        // 6자리 랜덤 코드 생성
        const code = Math.random().toString(36).substring(2, 8).toUpperCase();

        // 교과 코드 문서 생성 및 추가
        await addDoc(collection(db, COLLECTIONS.SUBJECT_CODES), {
          code,
          schoolId: teacher.schoolId,
          subjectId: subjectId,
          teacherId: teacher.uid,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        setSubjectCode(code);
        setOpenSubjectCodeDialog(true);
      }
    } catch (err) {
      console.error('교과 코드 생성 중 오류:', err);
      setError('교과 코드 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSubjectCodeDialog = () => {
    setOpenSubjectCodeDialog(false);
    setSubjectCode(null);
  };

  // 승인 대기 중인 학생 목록 가져오기
  useEffect(() => {
    if (!teacher?.uid || !teacher.subjectId) return;

    const loadPendingStudents = async () => {
      try {
        // student_requests 컬렉션에서 승인 대기 중인 학생 목록 가져오기
        const requestsRef = collection(db, COLLECTIONS.STUDENT_REQUESTS);
        const q = query(
          requestsRef,
          where('schoolId', '==', teacher.schoolId),
          where('subjectId', '==', teacher.subjectId),
          where('status', '==', 'pending')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
          const studentsData = snapshot.docs.map(doc => ({
            ...doc.data(),
            uid: doc.id,
            loginType: doc.data().loginType === 'social' ? doc.id : undefined
          })) as Student[];
          setPendingStudents(studentsData);
        });

        return () => unsubscribe();
      } catch (err) {
        console.error('승인 대기 학생 목록 로드 중 오류:', err);
        setError('승인 대기 학생 목록을 불러오는데 실패했습니다.');
      }
    };

    loadPendingStudents();
  }, [teacher?.uid, teacher?.subjectId]);

  // 학번 생성 함수
  const generateStudentId = (grade: number, classNum: number, number: number) => {
    return `${grade}${classNum.toString().padStart(2, '0')}${number.toString().padStart(2, '0')}`;
  };

  const handleApproveStudent = async (student: Student) => {
    try {
      setLoading(true);
      // 학생 승인 요청 문서 업데이트
      const studentRequestRef = doc(db, COLLECTIONS.STUDENT_REQUESTS, student.uid);
      const studentRequestDoc = await getDoc(studentRequestRef);
      if (!studentRequestDoc.exists()) {
        throw new Error('학생 승인 요청 문서를 찾을 수 없습니다.');
      }
      const studentRequestData = studentRequestDoc.data();

      await updateDoc(studentRequestRef, {
        status: 'approved',
        updatedAt: new Date()
      });

      // 학생 데이터 준비
      const studentData = {
        ...studentRequestData,
        uid: student.uid,
        status: 'approved',
        updatedAt: new Date(),
        assignedRoles: [],
        roles: [],
        points: 0
      };
      
      // 학급이 존재하는지 확인하고 없으면 새로 생성
      const classId = `${teacher.schoolId}_${student.grade}_${student.class}`;
      const classRef = doc(db, COLLECTIONS.CLASSES.replace('{schoolId}', teacher.schoolId), classId);
      const classDoc = await getDoc(classRef);
      
      console.log('classDoc:', classDoc);
      if (!classDoc.exists()) {
        // 학급이 없으면 새로 생성
        await setDoc(classRef, {
          id: classId,
          schoolId: teacher.schoolId,
          grade: student.grade,
          class: student.class,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      console.log('classId:', classId);
      // 학급 내 students 컬렉션에 학생 추가
      // const studentsPath = COLLECTIONS.STUDENTS
      //   .replace('{schoolId}', teacher.schoolId)
      //   .replace('{classId}', classId);
      // const classStudentsRef = collection(db, studentsPath);
      // 학번 생성
      const customId = generateStudentId(student.grade, student.class, student.number);
      await createApprovedStudentDoc({
        schoolId: teacher.schoolId,
        classId,
        student,
        studentData,
        studentId: customId
      });
      console.log('studentData:', studentData);
      // 로컬 상태 업데이트 - 승인된 학생을 students 목록에 추가
      setStudents(prev => {
        // 이미 존재하는지 확인
        const exists = prev.some(s => s.uid === student.uid);
        if (exists) {
          // 이미 존재하면 업데이트
          return prev.map(s => s.uid === student.uid ? { ...s, ...studentData } : s);
        } else {
          // 존재하지 않으면 추가
          return [...prev, { ...student, ...studentData } as Student];
        }
      });

      setSuccess('학생이 승인되었습니다.');
      // 승인된 학생을 목록에서 제거
      setPendingStudents(prev => prev.filter(s => s.uid !== student.uid));
    } catch (error) {
      console.error('학생 승인 중 오류:', error);
      setError('학생 승인 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectStudent = async (student: Student) => {
    try {
      setLoading(true);
      const studentRequestRef = doc(db, COLLECTIONS.STUDENT_REQUESTS, student.uid);
      await updateDoc(studentRequestRef, {
        status: 'rejected',
        updatedAt: new Date()
      });
      setSuccess('학생이 거절되었습니다.');
      // 거절된 학생을 목록에서 제거
      setPendingStudents(prev => prev.filter(s => s.uid !== student.uid));
    } catch (error) {
      console.error('학생 거절 중 오류:', error);
      setError('학생 거절 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 로그인하지 않은 경우 로딩 상태만 표시
  if (!currentUser) {
    return (
      <div style={getMakecodeArcadePageRootStyle(isMobile)}>
        <div style={{ display: 'flex', marginTop: '32px', marginBottom: '32px', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #FF6F0F', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        </div>
      </div>
    );
  }
  
  console.log('StudentManagement - currentUser:', currentUser);
  console.log('StudentManagement - teacher:', teacher);
  console.log('StudentManagement - teacher.homeroomClass:', teacher?.homeroomClass);

  return (
    <div style={getMakecodeArcadePageRootStyle(isMobile)}>
      <div style={{ marginTop: '32px', marginBottom: '32px' }}>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: 600, 
            lineHeight: '48px', 
            margin: 0,
            color: '#161616',
            fontFamily: 'Pretendard'
          }}>
            학생 관리
          </h1>
          {isLoggedIn && (
            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <Button
                variant="primary"
                leftIcon={<QrCodeIcon width={20} height={20} color="white" />}
                onClick={generateSubjectCode}
                disabled={loading || !teacher?.subjectId}
              >
                교과 코드 생성
              </Button>
            
              <Button
                variant="outline"
                leftIcon={<SettingIcon width={20} height={20} color="#FF6F0F" />}
                onClick={handleOpenSettingsDialog}
              >
                학급 설정
              </Button>
            </div>
          )}    
        </div>
      </div>

      {isLoggedIn ? (
        <div style={{ 
          marginBottom: '16px', 
          padding: '12px 16px', 
          backgroundColor: '#E3F2FD', 
          color: '#1976D2', 
          borderRadius: '8px',
          fontSize: '14px',
          fontFamily: 'Pretendard'
        }}>
          {teacher?.name} 선생님의 {teacher?.subject} 교과 학생 목록입니다. 학생을 추가하고 계정을 연동하면 학생들이 로그인하여 교과 활동에 참여할 수 있습니다.
        </div>
      ) : (
        <div style={{ 
          marginBottom: '16px', 
          padding: '12px 16px', 
          backgroundColor: '#FFF3E0', 
          color: '#F57C00', 
          borderRadius: '8px',
          fontSize: '14px',
          fontFamily: 'Pretendard'
        }}>
          로그인하지 않은 상태입니다. 로그인하면 학생 관리 기능을 사용할 수 있습니다.
        </div>
      )}

      {/* 승인 대기 중인 학생 목록 */}
      {isLoggedIn && pendingStudents.length > 0 && (
        <Card padding="16px" style={{ marginBottom: '24px' }}>
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: 600, 
            margin: '0 0 16px 0',
            color: '#161616',
            fontFamily: 'Pretendard'
          }}>
            승인 대기 중인 학생
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #F0F0F0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#161616' }}>이름</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#161616' }}>이메일</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#161616' }}>학번</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#161616' }}>학급</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#161616' }}>신청일</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#161616' }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {pendingStudents.map((student) => (
                  <tr key={student.uid} style={{ borderBottom: '1px solid #F0F0F0' }}>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#161616' }}>{student.name}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#161616' }}>{student.email}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#161616' }}>{student.number}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#161616' }}>{student.grade}학년 {student.class}반</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#161616' }}>
                      {student.createdAt instanceof Date 
                        ? student.createdAt.toLocaleDateString()
                        : (student.createdAt as any)?.toDate?.()?.toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                        <button
                          onClick={() => handleApproveStudent(student)}
                          style={{
                            padding: '8px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="승인"
                        >
                          <CheckCircleIcon width={20} height={20} color="#4CAF50" />
                        </button>
                        <button
                          onClick={() => handleRejectStudent(student)}
                          style={{
                            padding: '8px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="거절"
                        >
                          <CloseIcon width={20} height={20} color="#F44336" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', marginBottom: '24px' }}>
        <div style={{ minWidth: '200px', position: 'relative' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontSize: '14px', 
            fontWeight: 500,
            color: '#161616',
            fontFamily: 'Pretendard'
          }}>
            학급 선택
          </label>
          <select
            value={selectedClass ? `${selectedClass.grade}-${selectedClass.class}` : ''}
            onChange={(e) => {
              if (!e.target.value) {
                setSelectedClass(null);
                return;
              }
              const [grade, class_] = (e.target.value as string).split('-').map(Number);
              const selected = classSettings.find(
                setting => Number(setting.grade) === grade && Number(setting.class) === class_
              ) || null;
              console.log('Selected class:', selected);
              setSelectedClass(selected);
            }}
            style={{
              width: '100%',
              height: '48px',
              padding: '12px 16px',
              border: '1.2px solid #F0F0F0',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Pretendard',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">전체</option>
            {classSettings.map((setting) => (
              <option key={`${setting.grade}-${setting.class}`} value={`${setting.grade}-${setting.class}`}>
                {setting.grade}학년 {setting.class}반
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: 1 }}>
          <InputField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="학생 검색 (이름 또는 이메일)"
            iconImage="/images/icons/search.svg"
          />
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
          <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #FF6F0F', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        </div>
      ) : (
        <Card padding="0" style={{ maxHeight: '600px', overflow: 'auto' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                <tr style={{ borderBottom: '1px solid #F0F0F0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#161616', whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                      ref={(input) => {
                        if (input) input.indeterminate = selectedStudents.length > 0 && selectedStudents.length < filteredStudents.length;
                      }}
                      onChange={handleSelectAll}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#161616', whiteSpace: 'nowrap' }}>학년</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#161616', whiteSpace: 'nowrap' }}>반</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#161616', whiteSpace: 'nowrap' }}>번호</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#161616', whiteSpace: 'nowrap' }}>이름</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#161616', whiteSpace: 'nowrap' }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student, index) => (
                  <tr 
                    key={student.uid}
                    style={{ 
                      borderBottom: '1px solid #F0F0F0',
                      backgroundColor: index % 2 === 0 ? 'transparent' : '#F8F8F8'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#F0F0F0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'transparent' : '#F8F8F8';
                    }}
                  >
                    <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student.uid || '')}
                        onChange={() => handleSelectStudent(student.uid || '')}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#161616', whiteSpace: 'nowrap' }}>{student.grade}학년</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#161616', whiteSpace: 'nowrap' }}>{student.class}반</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#161616', whiteSpace: 'nowrap' }}>{student.number}번</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#161616', whiteSpace: 'nowrap' }}>{student.name}</td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                        <button
                          onClick={() => handleOpenDialog(student)}
                          style={{
                            padding: '8px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="수정"
                        >
                          <EditIcon width={20} height={20} color="#1976D2" />
                        </button>
                        <button
                          onClick={() => handleDelete(student)}
                          style={{
                            padding: '8px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="삭제"
                        >
                          <TrashIcon width={20} height={20} color="#F44336" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* 학생 추가/수정 다이얼로그 */}
      {openDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          fontFamily: 'Pretendard'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCloseDialog();
          }
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #F0F0F0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#161616' }}>
                {selectedStudent ? '학생 정보 수정' : '새 학생 추가'}
              </h2>
              <button
                onClick={handleCloseDialog}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#161616',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <InputField
                  label="학년"
                  value={formData.grade.toString()}
                  onChange={(value) => setFormData({ ...formData, grade: Number(value) || 0 })}
                  type="number"
                  required
                />
                <InputField
                  label="반"
                  value={formData.class.toString()}
                  onChange={(value) => setFormData({ ...formData, class: Number(value) || 0 })}
                  type="number"
                  required
                />
                <InputField
                  label="번호"
                  value={formData.number.toString()}
                  onChange={(value) => setFormData({ ...formData, number: Number(value) || 0 })}
                  type="number"
                  required
                />
                <InputField
                  label="이름"
                  value={formData.name}
                  onChange={(value) => setFormData({ ...formData, name: value })}
                  required
                />
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontSize: '14px', 
                    fontWeight: 500,
                    color: '#161616'
                  }}>
                    로그인 방식
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        value="local"
                        checked={formData.loginType === 'local'}
                        onChange={(e) => setFormData({ ...formData, loginType: e.target.value as 'local' | 'social' })}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', color: '#161616' }}>일반 로그인</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        value="social"
                        checked={formData.loginType === 'social'}
                        onChange={(e) => setFormData({ ...formData, loginType: e.target.value as 'local' | 'social' })}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', color: '#161616' }}>소셜 로그인</span>
                    </label>
                  </div>
                </div>
                {formData.loginType === 'social' && (
                  <InputField
                    label="이메일"
                    value={formData.email}
                    onChange={(value) => setFormData({ ...formData, email: value })}
                    type="email"
                    required
                  />
                )}
                {formData.loginType === 'local' && (
                  <>
                    <InputField
                      label="아이디"
                      value={formData.username}
                      onChange={(value) => setFormData({ ...formData, username: value })}
                      required
                    />
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontSize: '14px', 
                        fontWeight: 500,
                        color: '#161616'
                      }}>
                        비밀번호
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={formData.showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                          style={{
                            width: '100%',
                            height: '48px',
                            padding: '12px 40px 12px 16px',
                            border: '1.2px solid #F0F0F0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontFamily: 'Pretendard'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, showPassword: !formData.showPassword })}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {formData.showPassword ? (
                            <HideIcon width={20} height={20} color="#989898" />
                          ) : (
                            <ShowIcon width={20} height={20} color="#989898" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <Button
                        variant="outline"
                        onClick={generateRandomPassword}
                      >
                        비밀번호 자동 생성
                      </Button>
                    </div>
                  </>
                )}
                {formData.loginType === 'social' && (
                  <div style={{ 
                    marginTop: '16px', 
                    padding: '12px 16px', 
                    backgroundColor: '#E3F2FD', 
                    color: '#1976D2', 
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}>
                    소셜 로그인을 선택한 경우, 등록된 이메일 주소로 로그인하면 자동으로 학생 계정이 연동됩니다.
                  </div>
                )}
              </div>
              <div style={{ 
                padding: '16px 20px', 
                borderTop: '1px solid #F0F0F0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}>
                <Button variant="cancel" onClick={handleCloseDialog}>취소</Button>
                <Button type="submit" variant="primary">
                  {selectedStudent ? '수정' : '추가'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 학급 설정 다이얼로그 */}
      {openSettingsDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          fontFamily: 'Pretendard'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCloseSettingsDialog();
          }
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #F0F0F0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#161616' }}>
                클래스 설정
              </h2>
              <button
                onClick={handleCloseSettingsDialog}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#161616',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
              {getGradeRange().map(grade => (
                <div key={grade}>
                  <h3 style={{ 
                    margin: '0 0 12px 0', 
                    fontSize: '16px', 
                    fontWeight: 600, 
                    color: '#161616' 
                  }}>
                    {grade}학년
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {getClassRange(grade).map(classNum => (
                      <label
                        key={`${grade}_${classNum}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #F0F0F0',
                          backgroundColor: selectedClasses[`${grade}_${classNum}`] ? '#E3F2FD' : 'transparent'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedClasses[`${grade}_${classNum}`] || false}
                          onChange={() => handleClassSelection(grade, classNum)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '14px', color: '#161616' }}>{classNum}반</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ 
              padding: '16px 20px', 
              borderTop: '1px solid #F0F0F0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <Button variant="cancel" onClick={() => setOpenSettingsDialog(false)}>취소</Button>
              <Button variant="primary" onClick={handleSaveClassSettings}>저장</Button>
            </div>
          </div>
        </div>
      )}

      {/* 교과 코드 다이얼로그 */}
      {openSubjectCodeDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          fontFamily: 'Pretendard'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCloseSubjectCodeDialog();
          }
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #F0F0F0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#161616' }}>
                교과 코드
              </h2>
              <button
                onClick={handleCloseSubjectCodeDialog}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#161616',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ 
                margin: '0 0 16px 0', 
                fontSize: '14px', 
                color: '#161616',
                lineHeight: '20px'
              }}>
                아래 코드를 학생들에게 공유해주세요. 이 코드를 사용하여 학생들이 등록할 수 있습니다.
              </p>
              <div style={{
                padding: '16px',
                backgroundColor: '#F5F5F5',
                textAlign: 'center',
                fontFamily: 'monospace',
                fontSize: '24px',
                letterSpacing: '0.2em',
                borderRadius: '8px',
                color: '#161616',
                fontWeight: 600
              }}>
                {subjectCode}
              </div>
            </div>
            <div style={{ 
              padding: '16px 20px', 
              borderTop: '1px solid #F0F0F0',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <Button variant="primary" onClick={handleCloseSubjectCodeDialog}>닫기</Button>
            </div>
          </div>
        </div>
      )}

      {/* 성공/에러 메시지 */}
      {success && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '16px 24px',
          backgroundColor: '#4CAF50',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontFamily: 'Pretendard',
          fontSize: '14px',
          maxWidth: '400px'
        }}>
          <span>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '20px',
              padding: '0',
              marginLeft: 'auto'
            }}
          >
            ×
          </button>
        </div>
      )}
      
      {error && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '16px 24px',
          backgroundColor: '#F44336',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontFamily: 'Pretendard',
          fontSize: '14px',
          maxWidth: '400px'
        }}>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '20px',
              padding: '0',
              marginLeft: 'auto'
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default GameStudentManagement; 